import { useEffect, useState } from 'react'

import {
  NARROW_BREAKPOINT,
  resolveToastSurfaceId,
  TOAST_SURFACE_CONFIG,
  ToastSurfaceConfig,
  ToastSurfaceId
} from '@common/contexts/toastContext/config'

export interface ToastSurface extends ToastSurfaceConfig {
  surfaceId: ToastSurfaceId
  isNarrow: boolean
}

const MEDIA_QUERY = `(max-width: ${NARROW_BREAKPOINT - 1}px)`

const getIsNarrow = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false

  return window.matchMedia(MEDIA_QUERY).matches
}

/**
 * Resolves where toasts should be anchored and how many may be visible.
 *
 * Deliberately not built on `useWindowSize`, which setStates on every resize
 * event (one render per frame while dragging). Here the state only changes when
 * the narrow/wide boolean actually flips.
 */
const useToastSurface = (): ToastSurface => {
  const [isNarrow, setIsNarrow] = useState(getIsNarrow)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mql = window.matchMedia(MEDIA_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches)

    setIsNarrow(mql.matches)

    // Safari < 14 only supports the deprecated addListener API.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange)

      return () => mql.removeEventListener('change', onChange)
    }

    mql.addListener(onChange)

    return () => mql.removeListener(onChange)
  }, [])

  const surfaceId = resolveToastSurfaceId(isNarrow)

  return { surfaceId, isNarrow, ...TOAST_SURFACE_CONFIG[surfaceId] }
}

export default useToastSurface
